import React, { useMemo, useState } from "react";
import SectionTitle from "../components/SectionTitle";
import Seo from "../components/Seo";
import { useData } from "../context/DataContext";
import { X } from "lucide-react";

const ALL = "__ALL__";

const Galerie = () => {
  const { gallery, galleryCategories, loaded } = useData();
  const [open, setOpen] = useState(null);
  const [activeCat, setActiveCat] = useState(ALL);

  const orderedCategories = useMemo(() => {
    const set = new Set((galleryCategories || []).map((c) => c.name).filter(Boolean));
    // Include any category that is used on a photo but not listed
    for (const g of gallery || []) if (g?.category) set.add(g.category);
    return Array.from(set);
  }, [galleryCategories, gallery]);

  const filtered = useMemo(() => {
    if (activeCat === ALL) return gallery || [];
    return (gallery || []).filter((g) => (g?.category || "") === activeCat);
  }, [gallery, activeCat]);

  if (!loaded) return <div className="min-h-[60vh] flex items-center justify-center text-stone-500">Chargement...</div>;

  return (
    <>
      <Seo title="Galerie Photos — L'Élan Danse" description="Galerie photos de l'école de danse L'Élan Danse au Perray-en-Yvelines : cours, spectacles, coulisses." />

      <section className="py-20 px-6">
        <SectionTitle kicker="Souvenirs" title="Galerie Photos" subtitle="Quelques instantanés de cours, de spectacles et de coulisses qui font l'âme de l'école." />

        {orderedCategories.length > 0 && (
          <div className="max-w-6xl mx-auto mb-8 flex flex-wrap justify-center gap-2" data-testid="gallery-category-filter">
            <button onClick={() => setActiveCat(ALL)}
              data-testid="gallery-cat-all"
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${activeCat === ALL ? "bg-deep-rose text-white" : "bg-blush-light text-deep-rose hover:bg-blush"}`}>
              Toutes ({gallery.length})
            </button>
            {orderedCategories.map((cat) => {
              const count = gallery.filter((g) => g?.category === cat).length;
              return (
                <button key={cat} onClick={() => setActiveCat(cat)}
                  data-testid={`gallery-cat-${cat}`}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest transition-colors ${activeCat === cat ? "bg-deep-rose text-white" : "bg-blush-light text-deep-rose hover:bg-blush"}`}>
                  {cat} ({count})
                </button>
              );
            })}
          </div>
        )}

        <div className="max-w-6xl mx-auto columns-1 sm:columns-2 lg:columns-3 gap-4 space-y-4">
          {filtered.map((g, i) => (
            <button key={g.id || `${g.url}-${i}`} onClick={() => setOpen(g)} className="block w-full overflow-hidden rounded-2xl shadow-sm hover:shadow-xl transition-shadow group break-inside-avoid">
              <img src={g.url} alt={g.caption || `Galerie ${i + 1}`} className="w-full group-hover:scale-105 transition-transform duration-500" />
            </button>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-stone-500 italic py-8">Aucune photo dans cette catégorie.</div>
          )}
        </div>
      </section>

      {open && (
        <div className="fixed inset-0 z-50 bg-stone-900/85 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setOpen(null)}>
          <button className="absolute top-6 right-6 text-white p-2 rounded-full bg-white/10" onClick={() => setOpen(null)}><X /></button>
          <div className="max-w-full max-h-full flex flex-col items-center">
            <img src={open.url} alt={open.caption || "Photo"} className="max-h-[85vh] max-w-[92vw] object-contain rounded-xl" />
            {open.caption && <div className="mt-3 text-white/90 font-serif-display text-center">{open.caption}</div>}
          </div>
        </div>
      )}
    </>
  );
};

export default Galerie;
